import type { ReferenceGuide } from './types';
export const procurementGuide: ReferenceGuide = {
  slug: 'procurement-systems',
  category: 'Procurement',
  accent: 'procurement',
  title: 'Procurement Systems',
  subtitle: 'How purchase requests, RFQs, approvals, POs, and receiving run the buy-side lifecycle — what each document owns, where the hand-offs break, and what a disciplined procurement function actually looks like.',
  question: 'Can you prove the last three POs were authorised?',
  description: 'A vendor-neutral guide to procurement systems: PR vs PO, RFQs, approval workflows, purchase orders, and 3-way matching. What each document owns across the procure-to-pay lifecycle.',
  readMinutes: 11,
  pdf: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hpwwowkqzxwbasxuhq2t8d/69c4a78d-9834-47a7-b2b9-f26dfaf63c78/procurement-systems-reference-packet.pdf",
  cta: {
    headline: 'See where your procure-to-pay hand-offs break',
    button: 'Map your workflow'
  },
  sections: [{
    id: 'five-pillars',
    heading: 'The five pillars of procurement operations',
    tocLabel: 'The five pillars',
    standfirst: 'No single document turns a need into goods on the dock. Five steps share that responsibility — each owning a distinct moment in the buying lifecycle.',
    blocks: [{
      kind: 'pillars',
      columns: 5,
      items: [{
        tag: 'PR',
        title: 'Purchase Requests',
        layer: 'The need layer',
        body: 'An internal ask from operations, engineering, or the floor — "we need this, for this job, by this date."'
      }, {
        tag: 'RFQ',
        title: 'Request for Quote',
        layer: 'The sourcing layer',
        body: 'Multiple suppliers priced and compared on the same spec — price, lead time, terms.'
      }, {
        tag: 'APPR',
        title: 'Purchase Approvals',
        layer: 'The governance layer',
        body: 'Spend authority routed by amount, category, and policy — before any commitment is made.'
      }, {
        tag: 'PO',
        title: 'Purchase Orders',
        layer: 'The commitment layer',
        body: 'A legal contract issued to the supplier — quantity, price, terms, delivery.',
        emphasis: 'warn'
      }, {
        tag: 'RECV',
        title: 'Receiving',
        layer: 'The close layer',
        body: 'Goods inspected, counted, and matched to PO — the moment plan meets reality.',
        emphasis: 'accent'
      }]
    }]
  }, {
    id: 'lifecycle',
    heading: 'The procurement lifecycle at a glance',
    tocLabel: 'Lifecycle at a glance',
    standfirst: 'A single purchase request travels through nine distinct phases before goods are received and the invoice can be paid.',
    blocks: [{
      kind: 'lifecycle',
      stages: [{
        label: 'Stage 1',
        title: 'Request & Sourcing',
        steps: [{
          n: 1,
          title: 'Need identified',
          body: 'Operator, buyer, or planner flags a required material, part, or service.'
        }, {
          n: 2,
          title: 'Purchase request',
          body: 'PR created with spec, qty, need-by date, and cost center.'
        }, {
          n: 3,
          title: 'Sourcing / RFQ',
          body: 'Suppliers solicited; quotes collected and compared.'
        }],
        note: '**PR & RFQ drive this phase** — the question is what to buy, from whom, and at what price.'
      }, {
        label: 'Stage 2',
        title: 'Approve & Commit',
        steps: [{
          n: 4,
          title: 'Approval routed',
          body: 'Request routed by policy — amount, category, cost center, commodity.'
        }, {
          n: 5,
          title: 'PO issued',
          body: 'Purchase order generated and sent to the selected supplier.'
        }, {
          n: 6,
          title: 'Supplier confirms',
          body: 'Acknowledgment, updated ship date, and any substitutions captured.'
        }],
        note: '**Approvals gate the commitment** — the PO is the first irreversible step.'
      }, {
        label: 'Stage 3',
        title: 'Receive & Reconcile',
        steps: [{
          n: 7,
          title: 'Goods received',
          body: 'Receipt logged; quantity and condition inspected at the dock.'
        }, {
          n: 8,
          title: '3-way match',
          body: 'PO, receipt, and invoice reconciled; variances flagged.'
        }, {
          n: 9,
          title: 'Closed & paid',
          body: 'PO closed, inventory posted, invoice released to AP for payment.'
        }],
        note: '**Receiving closes the loop** — actuals feed inventory, costing, and supplier scorecards.',
        noteEmphasis: 'success'
      }]
    }]
  }, {
    id: 'pr-vs-po',
    heading: 'PR vs. PO',
    tocLabel: 'PR vs. PO',
    standfirst: 'Internal ask vs. external commitment — the two most confused documents in procurement.',
    blocks: [{
      kind: 'compare',
      cards: [{
        tag: 'PR',
        title: 'Purchase Request',
        body: 'The "internal ask" document. A request from inside the company asking to buy something. It is not a contract and does not obligate the company to anyone. It exists so spend can be reviewed and approved before the company commits.',
        bullets: ['Originates from the requester, not the buyer', 'Includes spec, quantity, need-by date, and cost center', 'Must move through the approval workflow before becoming a PO', 'Lives inside the company — never sent to a supplier']
      }, {
        tag: 'PO',
        title: 'Purchase Order',
        body: 'The "external commitment" document. A legally binding offer to a supplier to buy goods or services at a stated price, quantity, and date. Once the supplier acknowledges, both sides are on the hook.',
        bullets: ['Issued by procurement or the buyer — never by the requester directly', 'References an approved PR and a selected supplier quote', 'Carries price, qty, delivery date, payment terms, ship-to, GL code', 'Becomes binding once the supplier acknowledges or begins performance'],
        emphasis: 'warn'
      }],
      columns: ['Attribute', 'Purchase Request (PR)', 'Purchase Order (PO)'],
      rows: [['Purpose', 'Ask permission to spend', 'Commit the company to spend'], ['Audience', 'Internal only', 'External — sent to supplier'], ['Legal status', 'Non-binding', 'Legally binding once accepted'], ['Originator', 'Requester (eng, ops, floor, lab)', 'Procurement / Buyer'], ['Required inputs', 'Spec, qty, need-by, cost center', 'Approved PR + selected quote'], ['Approval needed', 'Yes — before it becomes a PO', 'Already approved at PR stage'], ['Key consumers', 'Managers, approvers, planners', 'Suppliers, AP, receiving'], ['Change cadence', 'Often edited before approval', 'Changed only via formal PO revision'], ['Core risk if absent', 'Maverick spend, no visibility into demand', 'No contract, no leverage on delivery or price']]
    }, {
      kind: 'callout',
      variant: 'warning',
      title: 'The core gap',
      body: "In most mid-market manufacturers, PRs live in email or spreadsheets, POs live in the ERP, and there's no clean bridge between them. Approvals happen over Slack or in hallways, quotes sit in inboxes, and when a PO is finally cut, nobody can point to the PR that started it — or prove the spend was authorised. That's how maverick spend, duplicate orders, and missed lead times get baked into the process."
    }]
  }, {
    id: 'rfqs-and-approvals',
    heading: 'RFQs & purchase approvals',
    tocLabel: 'RFQs & approvals',
    standfirst: 'The sourcing and governance layers that sit between "we need this" and "send the PO".',
    blocks: [{
      kind: 'compare',
      cards: [{
        tag: 'RFQ',
        title: 'Request for Quote',
        body: 'A structured solicitation sent to multiple suppliers for the same specification. It forces apples-to-apples comparison on price, lead time, payment terms, and MOQ — the only honest way to know whether the incumbent supplier is still the right one.',
        bullets: ['Issued to 2+ suppliers with identical spec and quantity', 'Captures price, lead time, terms, MOQ, and exceptions', 'Creates an auditable record of competitive sourcing', 'Required for spend over a threshold in most policies', 'Must be versioned — re-quotes and counters are normal']
      }, {
        tag: 'APPR',
        title: 'Purchase Approvals',
        body: 'The gate between an internal request and an external commitment. Good approval workflows route by the factors that actually matter — dollar amount, GL code, commodity, project, supplier risk — and capture every decision with a timestamp and approver.',
        bullets: ['Routed by amount, category, cost center, and project', 'Supports parallel approvers and delegation', 'Captures approver, timestamp, and any conditions', 'Should block PO issuance until fully approved', 'Creates the audit trail for SOX, ISO, and internal controls']
      }],
      columns: [],
      rows: []
    }, {
      kind: 'prose',
      paragraphs: ['Most manufacturers run RFQs in email: a spec gets pasted into a message, three suppliers get BCC’d, quotes come back in different formats over different days, and the buyer builds a side-by-side in a spreadsheet that nobody else will ever see.', 'The problem isn’t effort — it’s that each RFQ is re-invented from scratch, none of it is versioned, and the "why did we pick this supplier?" answer lives in someone’s head.']
    }, {
      kind: 'callout',
      variant: 'flag',
      title: 'Red flag',
      body: "If your last three supplier decisions were made over email and you can't reconstruct the competing quotes in under two minutes, you don't have a sourcing process — you have supplier relationships. Those are valuable, but they are not a control."
    }, {
      kind: 'prose',
      paragraphs: ['Effective approval workflows have three properties: the right routing — policy thresholds baked in so no one has to remember them; visible state — any requester, approver, or buyer can see exactly where a request is sitting and with whom; and an immutable trail — the approver, timestamp, and comment are captured the instant a decision is made, and cannot be rewritten later.']
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'Bottom line',
      body: 'RFQs define what to buy and from whom at what price. Approvals define who said yes and under what authority. Together they are the evidence layer that separates a procurement function from a purchasing clerk — and the part of the process that auditors, finance, and the board actually care about.'
    }]
  }, {
    id: 'full-lifecycle',
    heading: 'The full procurement lifecycle',
    tocLabel: 'The full lifecycle',
    standfirst: 'Procurement is a sequence of hand-offs between requesting, sourcing, approving, committing, and receiving. Each phase has a primary document in the driver’s seat, but all five layers are active.',
    blocks: [{
      kind: 'lifecycle',
      stages: [{
        label: 'Phase 1',
        title: 'Request (PR)',
        steps: [{
          n: 1,
          title: 'Need identified',
          body: 'Inventory min/max trip, job kitting gap, or capex request.'
        }, {
          n: 2,
          title: 'PR created',
          body: 'Spec, qty, need-by, cost center, project, attachments.'
        }, {
          n: 3,
          title: 'PR reviewed',
          body: 'Category buyer triages, de-duplicates, enriches with prior-order history.'
        }],
        note: '**Requester owns this phase.** Procurement triages; no supplier is engaged yet.'
      }, {
        label: 'Phase 2',
        title: 'Sourcing (RFQ)',
        steps: [{
          n: 4,
          title: 'Suppliers selected',
          body: 'Approved vendor list filtered by commodity, location, risk tier.'
        }, {
          n: 5,
          title: 'RFQs issued',
          body: 'Identical spec sent to 2+ suppliers; response deadline set.'
        }, {
          n: 6,
          title: 'Quotes compared',
          body: 'Price, lead time, terms normalised side-by-side; supplier selected.'
        }],
        note: '**Procurement drives sourcing.** RFQ creates the auditable competitive record.'
      }, {
        label: 'Phase 3',
        title: 'Approval & Commitment (APPR & PO)',
        steps: [{
          n: 7,
          title: 'Approval routed',
          body: 'Amount, GL, category, project determine the approver chain.'
        }, {
          n: 8,
          title: 'PO generated',
          body: 'PO created from approved PR + selected quote; ship-to, terms, GL.'
        }, {
          n: 9,
          title: 'Supplier acknowledges',
          body: 'Acknowledgment, confirmed ship date, substitutions if any.'
        }],
        note: '**Approvals gate the external commit.** PO is the first legally binding artifact.'
      }, {
        label: 'Phase 4',
        title: 'Receive & Close (RECV)',
        steps: [{
          n: 10,
          title: 'Goods received',
          body: 'Receipt logged at dock; qty, lot, condition, cert-of-conformance checked.'
        }, {
          n: 11,
          title: '3-way match',
          body: 'PO to receipt to invoice reconciled; variances flagged for disposition.'
        }, {
          n: 12,
          title: 'PO closed',
          body: 'Inventory posted, invoice released to AP, supplier scorecard updated.'
        }],
        note: '**Receiving closes the loop.** Actuals flow back to inventory, costing, and supplier analytics.',
        noteEmphasis: 'success'
      }]
    }, {
      kind: 'prose',
      paragraphs: ['In practice, most manufacturers have at least one broken hand-off in this chain. PRs live in spreadsheets, so the buyer only sees them when someone walks over. RFQs live in email, so there’s no record of who was asked or what they quoted. Approvals happen verbally, so the audit trail is a memory. Receiving is logged on paper at the dock and entered into the ERP three days later, so inventory is always wrong.', 'Each gap is a maverick-spend risk and an inventory risk — and they compound. A procurement function where all five layers are siloed is a function running on relationships, not systems.']
    }]
  }, {
    id: 'capability-matrix',
    heading: 'Capability ownership matrix',
    tocLabel: 'Capability matrix',
    standfirst: 'Which document — and which system — owns which part of the procure-to-pay lifecycle.',
    blocks: [{
      kind: 'matrix',
      columns: ['PR', 'RFQ', 'Approvals', 'PO', 'Receiving'],
      rows: [{
        capability: 'Need capture & demand signal',
        values: [2, 0, 0, 0, 0]
      }, {
        capability: 'Spec, qty, need-by definition',
        values: [2, 1, 0, 1, 0]
      }, {
        capability: 'Cost center & GL coding',
        values: [2, 0, 1, 1, 0]
      }, {
        capability: 'Approved vendor list enforcement',
        values: [1, 2, 1, 0, 0]
      }, {
        capability: 'Competitive quote solicitation',
        values: [0, 2, 0, 0, 0]
      }, {
        capability: 'Quote normalisation & comparison',
        values: [0, 2, 0, 0, 0]
      }, {
        capability: 'Spend threshold routing',
        values: [0, 0, 2, 0, 0]
      }, {
        capability: 'Approver capture & audit trail',
        values: [0, 0, 2, 0, 0]
      }, {
        capability: 'Policy & segregation-of-duties',
        values: [0, 0, 2, 1, 0]
      }, {
        capability: 'Legally binding commitment',
        values: [0, 0, 0, 2, 0]
      }, {
        capability: 'Price, terms, ship-to, delivery',
        values: [0, 1, 0, 2, 0]
      }, {
        capability: 'Supplier acknowledgment & ship date',
        values: [0, 0, 0, 2, 1]
      }, {
        capability: 'Dock receipt & condition check',
        values: [0, 0, 0, 0, 2]
      }, {
        capability: 'Lot / serial / COA capture',
        values: [0, 0, 0, 0, 2]
      }, {
        capability: '3-way match (PO/Receipt/Invoice)',
        values: [0, 0, 0, 1, 2]
      }, {
        capability: 'Variance & short-ship disposition',
        values: [0, 0, 1, 1, 2]
      }, {
        capability: 'Inventory & costing post-back',
        values: [0, 0, 0, 0, 2]
      }, {
        capability: 'Supplier performance scorecard',
        values: [0, 1, 0, 1, 2]
      }]
    }, {
      kind: 'callout',
      variant: 'tip',
      title: 'How to read this',
      body: 'No single document owns the full procure-to-pay lifecycle. PRs capture demand; RFQs enforce competition; Approvals control spend authority; POs create the supplier commitment; Receiving closes the loop and feeds inventory and accounting. A disciplined procurement function is really about the integration between these five layers — not any one document in isolation.'
    }]
  }, {
    id: 'where-thruline-fits',
    heading: 'Where Thruline fits in',
    tocLabel: 'Where Thruline fits',
    blocks: [{
      kind: 'thrulineFit',
      intro: 'Thruline was built for the integration gap that exists in nearly every mid-market manufacturer — the space between the ERP’s purchasing module and the reality on the floor, where PRs live in spreadsheets, RFQs live in email, and approvals live in memory. Depending on your current stack, Thruline plugs in one of two ways.',
      scenarios: [{
        n: 1,
        condition: 'If you already have an ERP with purchasing in place',
        headline: 'Thruline is the request, sourcing, and approval layer that feeds clean POs into your ERP',
        body: 'Thruline sits in front of your ERP, holding PRs, RFQs, quote comparisons, and the approval chain as the single source of truth. Requests are sourced against your approved vendor list, routed through policy-based approvals, and — only once fully approved — converted into a PO in the ERP automatically.',
        flow: [{
          label: 'Request & source',
          title: 'Thruline',
          body: 'PRs, RFQs, quotes, approvals',
          owned: true
        }, {
          label: 'Commit & post',
          title: 'ERP Purchasing',
          body: 'POs, inventory, AP, GL'
        }, {
          label: 'Receive',
          title: 'Thruline (floor)',
          body: 'Dock receipt, 3-way match, variance',
          owned: true
        }],
        flowConnectors: ['sync', 'sync'],
        footnote: 'The connection is bidirectional: when a supplier confirms a new ship date or a receipt turns up short, Thruline syncs that state back to both the ERP and the requester automatically.'
      }, {
        n: 2,
        condition: 'If your procurement is still spreadsheets, email, and hallway approvals',
        headline: 'Thruline can own the full procurement layer in one place',
        body: 'PRs are structured and searchable, RFQs are sent and compared in one place, approvals are routed by policy with a real audit trail, POs are generated and sent to suppliers, and receipts are captured at the dock with lot, serial, and COA data.'
      }],
      solutions: [{
        label: 'Explore Purchasing',
        to: '/solutions/purchasing'
      }, {
        label: 'Explore ReceivePro',
        to: '/solutions/receivepro'
      }]
    }, {
      kind: 'callout',
      variant: 'bottomLine',
      title: 'The bottom line',
      body: 'Whether you already run procurement inside an ERP or you’re still working off spreadsheets, Thruline meets you where you are. It either bridges your existing systems — turning messy front-end requests into clean, approved POs and closing the receiving loop — or it replaces the need for a standalone procurement platform altogether.'
    }]
  }]
};